import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Building2, User } from 'lucide-react'
import { useCreateClient } from '../../api/clients'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { cn } from '../../lib/utils'
import type { ClientType } from '@1130-studio/shared'

const formSchema = z.object({
  type: z.enum(['individual', 'company']),
  companyName: z.string().optional(),
  inn: z.string().optional(),
  ogrn: z.string().optional(),
  contactName: z.string().min(1, 'Имя обязательно'),
  email: z.string().email('Некорректный email').optional().or(z.literal('')),
  phone: z.string().optional(),
  telegram: z.string().optional(),
  whatsapp: z.string().optional(),
  legalAddress: z.string().optional(),
  notes: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

interface ClientFormProps {
  onClose: () => void
  onSuccess: () => void
  leadData?: {
    name: string
    email?: string
    phone?: string
    telegram?: string
  }
}

export default function ClientForm({ onClose, onSuccess, leadData }: ClientFormProps) {
  const [clientType, setClientType] = useState<ClientType>('company')
  const createClient = useCreateClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: 'company',
      contactName: leadData?.name || '',
      email: leadData?.email || '',
      phone: leadData?.phone || '',
      telegram: leadData?.telegram || '',
    },
  })

  const handleTypeChange = (type: ClientType) => {
    setClientType(type)
    setValue('type', type)
  }

  const onSubmit = async (data: FormData) => {
    try {
      await createClient.mutateAsync({
        type: data.type,
        companyName: data.type === 'company' ? data.companyName : undefined,
        inn: data.inn || undefined,
        ogrn: data.ogrn || undefined,
        contactName: data.contactName,
        email: data.email || undefined,
        phone: data.phone || undefined,
        telegram: data.telegram || undefined,
        whatsapp: data.whatsapp || undefined,
        legalAddress: data.legalAddress || undefined,
        notes: data.notes || undefined,
      })
      onSuccess()
    } catch (error) {
      console.error('Failed to create client:', error)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative w-full max-w-lg mx-4 border-border-medium z-10 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-bg-card z-10">
          <CardTitle>Новый клиент</CardTitle>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Client Type Toggle */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Тип клиента
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleTypeChange('company')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium border transition-colors',
                    clientType === 'company'
                      ? 'bg-accent-lime-dim border-accent-lime text-accent-lime'
                      : 'bg-bg-tertiary border-border-medium text-text-secondary hover:text-text-primary'
                  )}
                >
                  <Building2 className="w-4 h-4" />
                  Компания
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeChange('individual')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium border transition-colors',
                    clientType === 'individual'
                      ? 'bg-accent-lime-dim border-accent-lime text-accent-lime'
                      : 'bg-bg-tertiary border-border-medium text-text-secondary hover:text-text-primary'
                  )}
                >
                  <User className="w-4 h-4" />
                  Физлицо
                </button>
              </div>
            </div>

            {/* Company fields */}
            {clientType === 'company' && (
              <>
                <Input
                  {...register('companyName')}
                  label="Название компании"
                  placeholder="ООО «Компания»"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    {...register('inn')}
                    label="ИНН"
                    placeholder="123456789012"
                  />
                  <Input
                    {...register('ogrn')}
                    label="ОГРН"
                    placeholder="123456789012345"
                  />
                </div>
              </>
            )}

            {/* Contact person */}
            <Input
              {...register('contactName')}
              label={clientType === 'company' ? 'Контактное лицо *' : 'ФИО *'}
              placeholder="Иван Петров"
              error={errors.contactName?.message}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                {...register('email')}
                type="email"
                label="Email"
                placeholder="email@example.com"
                error={errors.email?.message}
              />
              <Input
                {...register('phone')}
                label="Телефон"
                placeholder="+7 999 123-45-67"
              />
            </div>

            <Input
              {...register('telegram')}
              label="Telegram"
              placeholder="@username"
            />

            <Input
              {...register('whatsapp')}
              label="WhatsApp"
              placeholder="+7 999 123-45-67"
            />

            {clientType === 'company' && (
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Юридический адрес
                </label>
                <textarea
                  {...register('legalAddress')}
                  rows={2}
                  className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-lime focus:ring-1 focus:ring-accent-lime/20 transition-all duration-200 resize-none"
                  placeholder="123456, г. Москва, ул. Примерная, д. 1"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Заметки
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-lime focus:ring-1 focus:ring-accent-lime/20 transition-all duration-200 resize-none"
                placeholder="Дополнительная информация..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" loading={createClient.isPending}>
                Создать
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
